"use client";
import { SchemaMarkup } from '../components/SEO/SchemaMarkup';
import { ENTITY } from '../data/entityData';
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import GeometricPattern from '../components/GeometricPattern';
import CrescentMoon from '../components/CrescentMoon';
import { COMPANY } from '../data/mock';
import { brandConfig } from '../brandConfig';
import { sacredRise, fadeSacred, goldLine, stagger, bloom, luxuriousFloat, premiumCardReveal, shimmerReveal } from '../lib/animations';

const GALLERY_VIDEOS = [
  { id: 1, type: 'image', src: 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=1600&h=1000&fit=crop&q=80', title: 'Masjid an-Nabawi', arabic: 'المسجد النبوي', category: 'Holy Sites' },
  { id: 2, type: 'image', src: 'https://images.unsplash.com/photo-1592326871020-04f58c1a52f3?w=1600&h=1000&fit=crop&q=80', title: 'The Holy Kaaba', arabic: 'الكعبة المشرفة', category: 'Holy Sites' },
  { id: 3, type: 'image', src: 'https://images.unsplash.com/photo-1584551246679-0daf3d275d0f?w=1600&h=1000&fit=crop&q=80', title: 'Masjid al-Haram Grand Architecture', arabic: 'عمارة المسجد الحرام', category: 'Journey' },
  { id: 4, type: 'image', src: 'https://images.unsplash.com/photo-1564769625905-50e93615e769?w=1600&h=1000&fit=crop&q=80', title: 'Pilgrims Performing Tawaf', arabic: 'حجاج يؤدون الطواف', category: 'Worship' },
  { id: 5, type: 'image', src: 'https://images.unsplash.com/photo-1570527140771-020891229bb4?w=1600&h=1000&fit=crop&q=80', title: 'Sacred Mosque Interior', arabic: 'داخل المسجد', category: 'Community' },
  { id: 6, type: 'image', src: 'https://images.unsplash.com/photo-1565552621379-3f03b22b7244?w=1600&h=1000&fit=crop&q=80', title: 'Medina Umbrellas at Twilight', arabic: 'مظلات المدينة عند الغسق', category: 'Holy Sites' },
];

const CATEGORIES = ['All', 'Journey', 'Worship', 'Holy Sites', 'Community'];

function VideoCard({ video, index, onPlay }) {
  const vidRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!vidRef.current) return;
    if (isHovered) {
      vidRef.current.play().catch(() => {});
    } else {
      vidRef.current.pause();
      vidRef.current.currentTime = 0;
    }
  }, [isHovered]);

  return (
    <motion.div
      variants={premiumCardReveal}
      custom={index}
      className="gallery-card hover-float"
      style={{
        position: 'relative', overflow: 'hidden', cursor: 'pointer',
        background: '#0F2040', aspectRatio: video.type === 'image' ? '21/9' : '9/16',
        borderRadius: 16,
        borderBottom: isHovered ? '4px solid #C9A84C' : '4px solid transparent',
        boxShadow: isHovered ? '0 25px 50px rgba(10, 22, 40, 0.4), 0 0 25px rgba(201,168,76,0.3)' : '0 10px 30px rgba(0,0,0,0.5)',
        transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onPlay(video)}
    >
      {video.type === 'image' ? (
        <img
          src={video.src}
          alt={video.title}
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)',
          }}
        />
      ) : (
        <video
          ref={vidRef}
          src={video.src}
          poster={video.poster}
          muted
          playsInline
          loop
          preload="none"
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)',
          }}
        />
      )}

      {/* Gradient overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: isHovered
          ? 'linear-gradient(to top, rgba(10,22,40,0.9) 0%, rgba(10,22,40,0.2) 40%, transparent 70%)'
          : 'linear-gradient(to top, rgba(10,22,40,0.75) 0%, rgba(10,22,40,0.15) 50%, transparent 100%)',
        transition: 'background 0.5s ease',
      }} />

      {/* Play button */}
      {video.type !== 'image' && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: `translate(-50%, -50%) scale(${isHovered ? 1 : 0.8})`,
          opacity: isHovered ? 1 : 0.6,
          width: 64, height: 64, borderRadius: '50%',
          background: 'rgba(201,168,76,0.9)', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          boxShadow: isHovered ? '0 8px 32px rgba(201,168,76,0.4)' : '0 4px 16px rgba(0,0,0,0.3)',
        }}>
          <Play size={24} fill="#0A1628" color="#0A1628" style={{ marginLeft: 3 }} />
        </div>
      )}

      {/* Category badge */}
      <div style={{
        position: 'absolute', top: 16, left: 16,
        background: 'rgba(27,107,58,0.85)', backdropFilter: 'blur(8px)',
        padding: '6px 16px', fontFamily: 'Inter', fontWeight: 600,
        color: '#FBF8F0', fontSize: 10, letterSpacing: '0.15em',
        textTransform: 'uppercase', borderRadius: 20,
        border: '1px solid rgba(201, 168, 76, 0.3)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
      }}>
        {video.category}
      </div>

      {/* Title area */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, padding: '28px 24px',
        transform: `translateY(${isHovered ? '0' : '10px'})`,
        opacity: isHovered ? 1 : 0.85,
        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}>
        <div className="arabic-text" style={{
          fontStyle: 'italic', color: '#C9A84C', fontSize: 15, marginBottom: 6,
          textShadow: '0 2px 4px rgba(0,0,0,0.5)'
        }}>
          {video.arabic}
        </div>
        <div style={{
          fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 20,
          lineHeight: 1.25, textShadow: '0 2px 8px rgba(0,0,0,0.5)'
        }}>
          {video.title}
        </div>
      </div>
    </motion.div>
  );
}

function VideoLightbox({ video, onClose, onPrev, onNext }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(10,22,40,0.97)', backdropFilter: 'blur(20px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 24, right: 24, zIndex: 10,
          width: 48, height: 48, borderRadius: '50%',
          background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all 0.3s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,168,76,0.3)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(201,168,76,0.15)'; }}
      >
        <X size={22} color="#C9A84C" />
      </button>

      {/* Prev/Next arrows */}
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="lightbox-nav"
        style={{
          position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 10,
          width: 52, height: 52, borderRadius: '50%',
          background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all 0.3s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,168,76,0.25)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(201,168,76,0.1)'; }}
      >
        <ChevronLeft size={24} color="#C9A84C" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="lightbox-nav"
        style={{
          position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 10,
          width: 52, height: 52, borderRadius: '50%',
          background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all 0.3s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,168,76,0.25)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(201,168,76,0.1)'; }}
      >
        <ChevronRight size={24} color="#C9A84C" />
      </button>

      {/* Video player */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 25 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '85vw', maxWidth: 900, position: 'relative',
          borderTop: '3px solid #C9A84C',
        }}
      >
        {video.type === 'image' ? (
          <img
            key={video.src}
            src={video.src}
            alt={video.title}
            style={{
              width: '100%', maxHeight: '75vh', objectFit: 'contain',
              background: '#000',
            }}
          />
        ) : (
          <video
            ref={videoRef}
            key={video.src}
            src={video.src}
            poster={video.poster}
            autoPlay
            controls
            muted={true}
            playsInline
            style={{
              width: '100%', maxHeight: '75vh', objectFit: 'contain',
              background: '#000',
            }}
          />
        )}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 4px 8px',
        }}>
          <div>
            <div className="arabic-text" style={{
              fontStyle: 'italic', color: '#C9A84C', fontSize: 16,
            }}>
              {video.arabic}
            </div>
            <div style={{
              fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 22,
              marginTop: 4,
            }}>
              {video.title}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxVideo, setLightboxVideo] = useState(null);

  const filteredVideos = activeCategory === 'All'
    ? GALLERY_VIDEOS
    : GALLERY_VIDEOS.filter(v => v.category === activeCategory);

  const currentIndex = lightboxVideo
    ? filteredVideos.findIndex(v => v.id === lightboxVideo.id)
    : -1;

  const handlePrev = () => {
    const prevIndex = (currentIndex - 1 + filteredVideos.length) % filteredVideos.length;
    setLightboxVideo(filteredVideos[prevIndex]);
  };

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % filteredVideos.length;
    setLightboxVideo(filteredVideos[nextIndex]);
  };
  return (
    <PageWrapper>
      <SchemaMarkup
        pageTitle={`Gallery | ${brandConfig.companyName} Sacred Journeys`}
        pageDescription="View photos of our pilgrims, hotels in Mecca and Medina, and the beautiful sacred sites of Islam from our previous Umrah journeys."
        pageUrl="/gallery"
      />
      {/* HERO */}
      <section style={{
        position: 'relative', minHeight: '55vh', overflow: 'hidden',
        background: '#0A1628', display: 'flex', alignItems: 'center',
      }}>
        {/* Ambient background glow */}
        <div className="ambient-glow-left" />
        <div className="ambient-glow-right" />

        {/* Background image & overlay */}
        <img
          src="https://images.unsplash.com/photo-1592326871020-04f58c1a52f3?w=1920&h=600&fit=crop&q=80"
          alt="Gallery Hero"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,22,40,0.88)' }} />
        <GeometricPattern color="#C9A84C" opacity={0.08} size={100} />

        {/* Hero content */}
        <div style={{
          position: 'relative', zIndex: 2, maxWidth: 'var(--container)',
          margin: '0 auto', padding: '140px var(--gutter) 80px', width: '100%',
        }}>
          <motion.div initial="hidden" animate="visible" variants={luxuriousFloat} custom={1}
            style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="floating-moon">
              <CrescentMoon size={28} color="#C9A84C" />
            </div>
            <span className="premium-glass" style={{
              fontFamily: 'Inter', fontWeight: 600, color: '#C9A84C',
              fontSize: 12, letterSpacing: '0.25em', textTransform: 'uppercase',
              padding: '6px 18px', borderRadius: 20
            }}>
              Visual Journey
            </span>
          </motion.div>

          <motion.h1 initial="hidden" animate="visible" variants={luxuriousFloat} custom={2}
            style={{
              fontFamily: 'Amiri, serif', fontWeight: 700, fontSize: 'var(--h1)',
              lineHeight: 1.15, margin: 0, color: '#FFFFFF', marginTop: 24,
            }}>
            Sacred Moments,<br />
            <span className="gold-shimmer">Captured Forever.</span>
          </motion.h1>

          <motion.div className="arabic-text" initial="hidden" animate="visible" variants={fadeSacred} custom={4}
            style={{ fontStyle: 'italic', color: 'rgba(201,168,76,0.9)', fontSize: 'var(--arabic)', marginTop: 20 }}>
            لحظات مقدسة — محفوظة إلى الأبد
          </motion.div>

          <motion.p initial="hidden" animate="visible" variants={luxuriousFloat} custom={4}
            style={{
              fontFamily: 'Inter', color: '#FBF8F0', fontSize: 'var(--body-lg)',
              lineHeight: 1.85, maxWidth: 560, marginTop: 24, opacity: 0.9
            }}>
            Relive the beauty and devotion of the sacred journey through the eyes of our pilgrims. Every frame tells a story of faith, immaculately preserved.
          </motion.p>
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <section style={{ background: '#FBF8F0', padding: 'var(--section) 0' }}>
        <div style={{ maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          {/* Section header */}
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <CrescentMoon size={40} color="#C9A84C" animate />
            </div>
            <div style={{
              fontFamily: 'Inter', fontWeight: 500, color: '#1B6B3A', fontSize: 11,
              letterSpacing: '0.3em', textTransform: 'uppercase',
            }}>
              — Video Gallery —
            </div>
            <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sacredRise}
              style={{
                fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628',
                fontSize: 'var(--h2)', marginTop: 14, lineHeight: 1.1,
              }}>
              Through the Eyes<br /><span style={{ color: '#1B6B3A' }}>of Our Pilgrims.</span>
            </motion.h2>
            <div className="arabic-text" style={{
              fontStyle: 'italic', color: 'rgba(201,168,76,0.8)', fontSize: 18, marginTop: 8,
            }}>
              من خلال عيون حجاجنا
            </div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={goldLine}
              style={{ width: 80, height: 1, background: '#C9A84C', margin: '32px auto' }} />
          </div>

          {/* Filter pills */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sacredRise}
            style={{
              display: 'flex', justifyContent: 'center', gap: 10,
              flexWrap: 'wrap', marginBottom: 48,
            }}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  fontFamily: 'Inter', fontWeight: 600, fontSize: 12,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  padding: '10px 24px', cursor: 'pointer',
                  border: activeCategory === cat ? '2px solid #1B6B3A' : '2px solid rgba(201,168,76,0.3)',
                  background: activeCategory === cat ? '#1B6B3A' : 'transparent',
                  color: activeCategory === cat ? '#FBF8F0' : '#0A1628',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  if (activeCategory !== cat) {
                    e.currentTarget.style.borderColor = '#1B6B3A';
                    e.currentTarget.style.color = '#1B6B3A';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeCategory !== cat) {
                    e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)';
                    e.currentTarget.style.color = '#0A1628';
                  }
                }}
              >
                {cat}
              </button>
            ))}
          </motion.div>

          {/* Video grid — masonry-style */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial="hidden" animate="visible"
              variants={stagger(0.1)}
              className="gallery-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 24,
              }}
            >
              {filteredVideos.map((video, i) => (
                <motion.div
                  key={video.id}
                  style={{
                    gridColumn: video.type === 'image' ? '1 / -1' : 'span 1',
                  }}
                >
                  <VideoCard
                    video={video}
                    index={i}
                    onPlay={setLightboxVideo}
                  />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Video count */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeSacred}
            style={{ textAlign: 'center', marginTop: 48 }}>
            <span style={{
              fontFamily: 'Inter', fontWeight: 500, color: '#8B7355',
              fontSize: 13, letterSpacing: '0.08em',
            }}>
              {brandConfig.companyName} Gallery
            </span>
          </motion.div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section style={{
        background: '#0A1628', padding: 'clamp(80px, 10vw, 120px) 0',
        position: 'relative', overflow: 'hidden',
        borderTop: '1px solid rgba(201,168,76,0.2)'
      }}>
        <div className="ambient-glow-left" />
        <div className="ambient-glow-right" />
        <GeometricPattern color="#C9A84C" opacity={0.08} size={100} />
        <div style={{
          position: 'relative', zIndex: 2, maxWidth: 800, margin: '0 auto',
          padding: '0 var(--gutter)', textAlign: 'center',
        }}>
          <div className="floating-moon">
            <CrescentMoon size={48} color="#C9A84C" />
          </div>
          <h2 style={{
            fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0',
            fontSize: 'var(--h2)', marginTop: 28, lineHeight: 1.15,
          }}>
            Ready for Your Own <span className="gold-shimmer">Sacred Journey?</span>
          </h2>
          <div className="arabic-text" style={{
            fontStyle: 'italic', color: 'rgba(201,168,76,0.9)', fontSize: 24, marginTop: 14,
          }}>
            هل أنت مستعد لرحلتك المقدسة؟
          </div>
          <p style={{
            fontFamily: 'Inter', color: '#FBF8F0', fontSize: 'var(--body-lg)',
            marginTop: 20, lineHeight: 1.85, opacity: 0.9, maxWidth: 640, margin: '20px auto 0'
          }}>
            Join thousands of pilgrims who trusted us with their journey. Let us create beautiful, everlasting memories for you too.
          </p>
          <div style={{ marginTop: 42, display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/packages" style={{ textDecoration: 'none' }}>
              <button className="gold-pulse btn-premium" style={{
                fontFamily: 'Amiri', fontWeight: 700, fontSize: 17,
                padding: '16px 42px', background: '#C9A84C', color: '#0A1628',
                border: 'none', cursor: 'pointer', letterSpacing: '0.05em',
                borderRadius: 9999
              }}>
                View Packages
              </button>
            </a>
            <a href={`tel:${COMPANY.phoneIntl}`} style={{ textDecoration: 'none' }}>
              <button className="btn-premium" style={{
                fontFamily: 'Inter', fontWeight: 500, fontSize: 15,
                padding: '16px 36px', background: 'rgba(201,168,76,0.1)', color: '#FBF8F0',
                border: '2px solid rgba(201,168,76,0.5)', cursor: 'pointer',
                borderRadius: 9999
              }}>
                Call {COMPANY.phoneDisplay}
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightboxVideo && (
          <VideoLightbox
            video={lightboxVideo}
            onClose={() => setLightboxVideo(null)}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        )}
      </AnimatePresence>

      <style>{`
        .gallery-grid .gallery-card {
          aspect-ratio: 9/14;
        }
        .gallery-featured .gallery-card {
          aspect-ratio: 16/10 !important;
          height: 100%;
        }
        @media (max-width: 1023px) {
          .gallery-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .gallery-featured {
            grid-column: span 2 !important;
            grid-row: span 1 !important;
          }
          .gallery-featured .gallery-card {
            aspect-ratio: 16/9 !important;
          }
        }
        @media (max-width: 640px) {
          .gallery-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .gallery-featured {
            grid-column: span 1 !important;
          }
          .gallery-featured .gallery-card {
            aspect-ratio: 16/10 !important;
          }
          .gallery-card {
            aspect-ratio: 16/10 !important;
          }
          .lightbox-nav {
            display: none !important;
          }
        }
      `}</style>
    </PageWrapper>
  );
}
